const CHUNK_RELOAD_STORAGE_KEY = "raas:chunk-reload-at";
const CHUNK_RELOAD_WINDOW_MS = 60_000;
const CIP_TICKET_STORAGE_KEY = "raas:cip-ticket-id";

type ReloadTarget = Pick<Window, "addEventListener" | "removeEventListener">;

/**
 * Reloads once when Vite fails to fetch a lazy chunk (deploy, sleeping host, or a dropped download).
 * A second failure within 60s is left for the SDK error screen, so a bad chunk cannot reload forever.
 * The magic-link session lives in sessionStorage and survives the reload.
 */
export const installChunkReload = (options?: {
  storage?: Pick<Storage, "getItem" | "setItem">;
  now?: () => number;
  reload?: () => void;
  target?: ReloadTarget;
}): (() => void) => {
  const storage = options?.storage ?? sessionStorage;
  const now = options?.now ?? (() => Date.now());
  const reload = options?.reload ?? (() => window.location.reload());
  const target = options?.target ?? window;

  const onPreloadError = (event: Event) => {
    try {
      const last = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY) || "0");
      if (Number.isFinite(last) && last > 0 && now() - last < CHUNK_RELOAD_WINDOW_MS) {
        return;
      }
      storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now()));
    } catch {
      return;
    }
    event.preventDefault();
    reload();
  };

  target.addEventListener("vite:preloadError", onPreloadError);
  return () => target.removeEventListener("vite:preloadError", onPreloadError);
};

/**
 * The CIP ticket id arrives in router state, which a reload drops.
 * Remember it so the chunk-reload (and the SDK "Reload" button) reopen the same ticket.
 * Kentucky then restores the step from its own last-route storage.
 */
export const rememberCipTicketId = (
  fromState: string | undefined,
  storage: Pick<Storage, "getItem" | "setItem"> = sessionStorage,
): string | undefined => {
  try {
    if (fromState) {
      storage.setItem(CIP_TICKET_STORAGE_KEY, fromState);
      return fromState;
    }
    return storage.getItem(CIP_TICKET_STORAGE_KEY) || undefined;
  } catch {
    return fromState;
  }
};

export const forgetCipTicketId = (
  storage: Pick<Storage, "removeItem"> = sessionStorage,
): void => {
  try {
    storage.removeItem(CIP_TICKET_STORAGE_KEY);
  } catch {
    /* private mode */
  }
};
